import { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { useLocationConfigContext } from "../../context/LocationConfigContext";
import { SectorTable } from "./components/SectorTable";
import CRUDSectorModal from "./CRUDSectorModal";
import { ETypeOfNotification, Modal, setNotification } from "medere-ui-framework";
import { Sector } from "../../../../interface/interface";
import { deleteSector, loadAllEstablishments, saveSector } from "../../../../servicesHooks/Location/useLocationConfigService";
import { useDispatch } from "react-redux";
import { ProgressSpinner } from "primereact/progressspinner";
import useResizeListener from "../../../../hooks/useResizeListener";
import LocationEstablishmentFilter from "../components/LocationEstablishmentFilter";
import useEstablishmentFilterSelection from "../../hooks/useEstablishmentFilterSelection";

export default function SectorView() {
    const isMobile = useResizeListener(768);
    const { establishments, orderedEstablishments, setEstablishments, setSectorsToEstablishments, setCallersToEstablishments } = useLocationConfigContext();
    const dispatch = useDispatch()

    const [openModal, setOpenModal] = useState(false);
    const [editSector, setEditSector] = useState<Sector | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const [openRemoveModal, setOpenRemoveModal] = useState(false);
    const { selectedEstablishmentIds, setSelectedEstablishmentIds } = useEstablishmentFilterSelection(isMobile);

    const updateSectors = (estIndex: number, newSectors: Sector[]) => { //Actualiza la lista de sectores de un determinado establecimiento
        setEstablishments((prev) => {
            const updated = [...prev];
            updated[estIndex] = { ...updated[estIndex], sectors: newSectors };
            return updated;
        });
    };

    const handleCreate = () => { //Crea el sector, en este caso si aparece habilitado el Combobox de Establecimientos
        setEditSector(null);
        setOpenModal(true);
    };

    const handleEdit = (sector: Sector) => { //Edita un sector en especifico, el cual se precargan sus datos en la modal. En este caso no aparece habilitado el Combobox de Establecimientos pues ya se sabe
        setEditSector(sector);
        setOpenModal(true);
    };

    const handleOpenRemoveModal = (sector: Sector) => {
        setEditSector(sector) //se usa para eliminar en este flujo
        setOpenRemoveModal(true)
    }
    const handleDelete = async (sector: Sector) => {
        if (!sector || !sector.sector || sector.sector <= 0) return;

        setIsLoading(true); //La eliminación de sector tarda demasiado como para no indicar con un loader
        deleteSector(sector.sector)
        .then(() => {
            const estIndex = establishments.findIndex(e => e.buildingid === sector.building);
            if (estIndex < 0) {
                console.error("índice inválido")
                return
            };

            const updatedSectors = establishments[estIndex].sectors.filter(s => s.sector !== sector.sector);
            updateSectors(estIndex, updatedSectors);

            const updatedEstablishments = [...establishments];
            updatedEstablishments[estIndex].sectors = updatedSectors;
            setSectorsToEstablishments(updatedEstablishments);

            setEditSector(null);
            setOpenRemoveModal(false);
            dispatch(setNotification({
                title: "Eliminado",
                desc: "Se eliminó el sector",
                icon: "pi pi-check-circle",
                typeOfNotification: ETypeOfNotification.INFO
            }));
        }).catch((err) => {
            console.error("Error al eliminar el sector:", err);
            if (err.response?.status === 409) {
                const msg = err?.response?.data?.message || "";
                if (err.response?.data === 'ASSOCIATED' || msg.includes("violates foreign key"))
                    dispatch(dispatch(setNotification({ title: "No se puede eliminar", desc: "El sector tiene dependencias (consultorios, puestos, llamadores o camas)", icon: "pi pi-exclamation-triangle", typeOfNotification: ETypeOfNotification.WARNING })));
            } else
                dispatch(setNotification({ title: "Error", desc: "No se pudo eliminar el sector", icon: "pi pi-times", typeOfNotification: ETypeOfNotification.ERROR }));
        }).finally(() => {
            setIsLoading(false);
        });
    };

    const handleSave = async (newSector: Sector, estId: number) => {
        try {
            await saveSector(newSector)
            .then(() => {
                loadAllEstablishments().then((res) => {
                    setEstablishments(res);
                    setCallersToEstablishments(res);
                }).catch((err) => {
                    console.error(err)
                    dispatch(setNotification({ title: "Ocurrió un error", desc: 'No se pudieron cargar los datos', icon: 'pi-times', typeOfNotification: ETypeOfNotification.ERROR }));
                });
            });
        } catch (e) {
            console.error("Error al guardar el sector:", e);
            return;
        }
        setEditSector(null);
        setOpenModal(false);
    };

    const establishmentsToRender = isMobile
        ? orderedEstablishments.filter(est => selectedEstablishmentIds.includes(est.buildingid))
        : orderedEstablishments;

    return (
        <div>
            <h1 className="title2">Sector</h1>
            <div className={`emr-border-bottom pb-2 ${isMobile ? "location-mobile-top-toolbar" : "flex flex-row justify-content-between"}`}>
                <p className="grey-color">Los sectores se asocian a los establecimientos creados.</p>
                <Button
                    id="buttonCreateSectorId"
                    icon="pi pi-plus-circle"
                    className={`buttonMain ${isMobile ? "location-mobile-create-btn" : "margin-create-button"}`}
                    label="Crear"
                    onClick={handleCreate}
                />
            </div>

            {isMobile && (
                <LocationEstablishmentFilter
                    establishments={orderedEstablishments}
                    selectedEstablishmentIds={selectedEstablishmentIds}
                    onChange={setSelectedEstablishmentIds}
                />
            )}

            
            {establishmentsToRender.map((est) => {
                const realIndex = establishments.findIndex(x => x.buildingid === est.buildingid);
                return (
                <div key={est.buildingid} className="my-5">
                    <div className="flex flex-row gap-4">
                        <h2 className="title3">{est.buildingname}</h2>
                        <p className="grey-color mt-1">{est.buildingaddress}</p>
                    </div>
                    <SectorTable
                        sectors={est.sectors}
                        onChange={(newSectors) => updateSectors(realIndex, newSectors)}
                        onEdit={(sector) => handleEdit(sector)}
                        onDelete={(sector) => handleOpenRemoveModal(sector)}
                    />
                </div>
                );
            })}

            {openModal && (
                <CRUDSectorModal
                    visible={openModal}
                    setVisible={setOpenModal}
                    onSubmit={handleSave}
                    sectorToEdit={editSector ?? undefined}
                    isCreateModeFromEstablishment={editSector === null}
                    establishmentOptions={isMobile ? establishmentsToRender : undefined}
                />
            )}

            <Modal className="location-mobile-editor-modal" visible={openRemoveModal} setVisible={setOpenRemoveModal} header={"Estás por eliminar el sector"} headerStyle={{ fontSize: '12px', textAlign: 'center' }} width='w-medium' closable={false}>
                <div>
                    <p className="grey-color">No podrás eliminar sectores que tengan asociados consultorios, puestos o llamadores.</p>
                    {isLoading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '40px' }}>
                            <ProgressSpinner style={{ width: '30px', height: '30px' }} strokeWidth="4" />
                        </div>
                    ) : (
                        <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'}}>
                            <Button id="buttonDeleteSectorId" label="Si, eliminar" onClick={() => handleDelete(editSector)} className="w-full buttonMain" />
                            <Button id="buttonCancelDeleteSectorId" label={"Cancelar"} onClick={() => setOpenRemoveModal(false)} className="w-full btnCancel2" />
                        </div>
                    )}
                </div>
            </Modal>
        </div>
    );
}


